import * as React from 'react';
import getClassNames from 'classnames';

interface RadioCardOwnProps {
  value: string;
  selectedValue?: string;
  label: string;
  secondaryLabel?: string;
  fieldName: string;
  onChange: (e: any) => void;
  classNames?: RadioCardClassNamesProps;
}

type RadioCardProps = RadioCardOwnProps;

type RadioCardClassNames = {
  radioCard: string;
  radioCardChecked: string;
  radioButton: string;
  indicator: string;
  label: string;
  labelPrimary: string;
  labelSecondary: string;
};

export type RadioCardClassNamesProps = {
  [K in keyof RadioCardClassNames]?: RadioCardClassNames[K];
};

const defaultClassNames: RadioCardClassNames = {
  radioCard: 'radio-card',
  radioCardChecked: 'radio-card--checked',
  radioButton: 'radio p-y-0 size-xl',
  indicator: 'radio-card-custom-radio-indicator',
  label: 'radio-card-label',
  labelPrimary: 'radio-card-label-primary',
  labelSecondary: 'radio-card-label-secondary',
};

const getClasses = (
  classNames?: RadioCardClassNamesProps,
): RadioCardClassNames => {
  if (!classNames) return defaultClassNames;

  return {
    radioCard: getClassNames(defaultClassNames.radioCard, classNames.radioCard),
    radioCardChecked: getClassNames(
      defaultClassNames.radioCardChecked,
      classNames.radioCardChecked,
    ),
    radioButton: getClassNames(
      defaultClassNames.radioButton,
      classNames.radioButton,
    ),
    indicator: getClassNames(defaultClassNames.indicator, classNames.indicator),
    label: getClassNames(defaultClassNames.label, classNames.label),
    labelPrimary: getClassNames(
      defaultClassNames.labelPrimary,
      classNames.labelPrimary,
    ),
    labelSecondary: getClassNames(
      defaultClassNames.labelSecondary,
      classNames.labelSecondary,
    ),
  };
};

export const RadioCard: React.FC<RadioCardProps> = (props) => {
  const {
    value,
    selectedValue,
    label,
    secondaryLabel,
    fieldName,
    onChange,
    classNames,
  } = props;
  const checked = value === selectedValue;
  const classes = getClasses(classNames);

  return (
    <div
      className={`${classes.radioCard} ${checked && classes.radioCardChecked}`}
      onClick={() => {
        onChange({ target: { value, name: fieldName, checked: checked } });
      }}
    >
      <label className={classes.radioButton} id={`${fieldName}_${value}_label`}>
        <input
          type="radio"
          id={`${fieldName}_${value}`}
          name={fieldName}
          value={value}
          checked={checked}
          onChange={onChange}
        />
        <span
          id={`${fieldName}_${value}_indicator`}
          className={classes.indicator}
        />
      </label>
      <div className={classes.label}>
        <div className={classes.labelPrimary}>{label}</div>
        {secondaryLabel && (
          <div className={classes.labelSecondary}>{secondaryLabel}</div>
        )}
      </div>
    </div>
  );
};

RadioCard.displayName = 'RadioCard';
